import { RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, setAdminToken } from '../../api/client'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type {
  AdminTrustAccountRow,
  AdminTrustPerson,
  AdminTrustReportRow,
  AdminTrustSummary,
  TrustReportCategory,
  TrustReportStatus,
} from '../../types/api'
import { AdminHeader } from './adminAccess'

const CATEGORY_LABEL: Record<TrustReportCategory, string> = {
  SAFETY: 'Safety',
  MINOR_SAFETY: 'Child safety',
  FRAUD: 'Fraud',
  INAPPROPRIATE: 'Inappropriate',
  IMPERSONATION: 'Impersonation',
  SPAM: 'Spam',
  FULFILMENT: 'Did not deliver',
  OTHER: 'Other',
}
const CATEGORIES = Object.keys(CATEGORY_LABEL) as TrustReportCategory[]

/** The two safety categories are drawn red so they stand out in a long list. */
const CATEGORY_TONE: Partial<Record<TrustReportCategory, string>> = {
  SAFETY: 'bg-red-100 text-red-800',
  MINOR_SAFETY: 'bg-red-100 text-red-800',
  FRAUD: 'bg-amber-100 text-amber-800',
}

const STATUS_LABEL: Record<TrustReportStatus, string> = {
  OPEN: 'Open',
  TRIAGED: 'Triaged',
  ACTIONED: 'Actioned',
  DISMISSED: 'Dismissed',
}
const STATUS_TONE: Record<TrustReportStatus, string> = {
  OPEN: 'bg-amber-100 text-amber-800',
  TRIAGED: 'bg-sky-100 text-sky-800',
  ACTIONED: 'bg-green-100 text-green-800',
  DISMISSED: 'bg-ink-100 text-ink-600',
}
const FILTERS: (TrustReportStatus | 'ALL')[] = ['ALL', 'OPEN', 'TRIAGED', 'ACTIONED', 'DISMISSED']

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-'

/** STUDENT -> Seeker, MINOR_SAFETY -> Minor safety. */
const humanize = (s: string) =>
  s === 'STUDENT' ? 'Seeker' : s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ')

function Pill({ tone, children }: { tone: string; children: ReactNode }) {
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{children}</span>
}

function Person({ p, phone = true }: { p: AdminTrustPerson; phone?: boolean }) {
  return (
    <div className="min-w-[10rem]">
      <div className="font-medium text-ink-900">{p.fullName}</div>
      <div className="text-xs text-ink-500">{humanize(p.entityKind)}</div>
      {p.email && <div className="break-all text-xs text-ink-600">{p.email}</div>}
      {phone && p.phone && <div className="text-xs text-ink-600">{p.phone}</div>}
    </div>
  )
}

function Tile({ label, value, sub, warn }: { label: string; value: number | undefined; sub?: string; warn?: boolean }) {
  return (
    <li className="card-pad">
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${warn ? 'text-amber-700' : 'text-ink-900'}`}>{value ?? '-'}</div>
      {sub && <div className="mt-0.5 text-xs text-ink-500">{sub}</div>}
    </li>
  )
}

/**
 * Reports and blocks across the network, for a super admin: what people have reported, who keeps
 * getting reported or blocked, and the triage of each report (triage, record an action, dismiss).
 * Blocks themselves are a participant's own choice, so this screen only counts them.
 */
export function AdminReportsPage() {
  const navigate = useNavigate()
  const [summary, setSummary] = useState<AdminTrustSummary | null>(null)
  const [tab, setTab] = useState<'reports' | 'accounts'>('reports')
  const [filter, setFilter] = useState<TrustReportStatus | 'ALL'>('OPEN')
  const [reports, setReports] = useState<AdminTrustReportRow[]>([])
  const [accounts, setAccounts] = useState<AdminTrustAccountRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  // The report whose "action taken" note is being typed, and the note so far.
  const [actionFor, setActionFor] = useState<string | null>(null)
  const [actionNote, setActionNote] = useState('')

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const fail = useCallback(
    (err: unknown, fallback: string) => {
      if (err instanceof ApiError && err.status === 401) signOut()
      else setError(apiErrorMessage(err, fallback))
    },
    [signOut],
  )

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await adminApi.trustSummary())
    } catch (err) {
      fail(err, 'Could not load the summary')
    }
  }, [fail])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [s, r, a] = await Promise.all([
        adminApi.trustSummary(),
        adminApi.trustReports(filter === 'ALL' ? undefined : filter),
        adminApi.trustAccounts(),
      ])
      setSummary(s)
      setReports(r)
      setAccounts(a)
    } catch (err) {
      fail(err, 'Could not load reports and blocks')
    } finally {
      setLoading(false)
    }
  }, [fail, filter])

  useEffect(() => {
    void load()
  }, [load])

  async function update(row: AdminTrustReportRow, body: Parameters<typeof adminApi.updateTrustReport>[1], done: string) {
    setBusyId(row.id)
    setError(null)
    setNotice(null)
    try {
      const saved = await adminApi.updateTrustReport(row.id, body)
      // Updated in place even if it no longer matches the filter, so the admin sees what changed.
      setReports((rs) => rs.map((r) => (r.id === saved.id ? saved : r)))
      setNotice(done)
      setActionFor(null)
      void loadSummary()
      void adminApi.trustAccounts().then(setAccounts, () => undefined)
    } catch (err) {
      fail(err, 'Could not save the change')
    } finally {
      setBusyId(null)
    }
  }

  const byCategory = summary
    ? CATEGORIES.filter((c) => (summary.reportsByCategory[c] ?? 0) > 0).map((c) => [c, summary.reportsByCategory[c]!] as const)
    : []

  const empty = (cols: number, text: string) => (
    <tr>
      <td colSpan={cols} className="px-4 py-6 text-center text-ink-500">
        {loading ? 'Loading...' : text}
      </td>
    </tr>
  )

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="trust" onSignOut={signOut} width="max-w-6xl">
        <button onClick={() => void load()} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-6xl space-y-5 px-4 py-6">
        <div>
          <h1 className="text-xl font-bold text-ink-900">Reports &amp; blocks</h1>
          <p className="mt-1 text-sm text-ink-500">
            What participants have reported about each other, and who is being blocked. Work the open reports first.
          </p>
        </div>

        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          <Tile label="Open reports" value={summary?.openReports} warn={(summary?.openReports ?? 0) > 0} />
          <Tile label="Total reports" value={summary?.totalReports} />
          <Tile label="Reported accounts" value={summary?.reportedAccounts} />
          <Tile
            label="Total blocks"
            value={summary?.totalBlocks}
            sub={summary ? `${summary.blocksLast30Days} in last 30 days` : undefined}
          />
          <Tile label="Blocked accounts" value={summary?.blockedAccounts} />
        </ul>

        {byCategory.length > 0 && (
          <div className="card-pad">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-500">Reports by category</div>
            <ul className="mt-2 flex flex-wrap gap-2">
              {byCategory.map(([c, n]) => (
                <li key={c} className="flex items-center gap-1.5 rounded-lg bg-ink-100 px-2.5 py-1 text-sm">
                  <span className="text-ink-700">{CATEGORY_LABEL[c]}</span>
                  <span className="font-semibold text-ink-900">{n}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <ErrorMessage message={error} />
        {notice && <p className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">{notice}</p>}

        <div className="flex gap-2 border-b border-ink-200">
          {(['reports', 'accounts'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              aria-pressed={tab === t}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold ${
                tab === t ? 'border-ink-900 text-ink-900' : 'border-transparent text-ink-500 hover:text-ink-900'
              }`}
            >
              {t === 'reports' ? 'Reports' : 'Accounts'}
            </button>
          ))}
        </div>

        {tab === 'reports' && (
          <>
            <div className="flex flex-wrap gap-2">
              {FILTERS.map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  aria-pressed={filter === f}
                  className={`rounded-full px-3 py-1 text-sm font-semibold ${
                    filter === f ? 'bg-ink-900 text-ink-50' : 'bg-white text-ink-600 hover:text-ink-900'
                  }`}
                >
                  {f === 'ALL' ? 'All' : STATUS_LABEL[f]}
                  {f !== 'ALL' && summary && <span className="ml-1 opacity-70">{summary.reportsByStatus[f] ?? 0}</span>}
                </button>
              ))}
            </div>

            <div className="card overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Category</th>
                    <th className="px-4 py-3">Reported</th>
                    <th className="px-4 py-3">Reported by</th>
                    <th className="px-4 py-3">Details</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reports.length === 0 &&
                    empty(7, filter === 'ALL' ? 'No reports yet.' : `No ${STATUS_LABEL[filter].toLowerCase()} reports.`)}
                  {reports.map((row) => {
                    const busy = busyId === row.id
                    const closed = row.status === 'ACTIONED' || row.status === 'DISMISSED'
                    return (
                      <tr key={row.id} className="border-b border-ink-100 align-top last:border-0">
                        <td className="whitespace-nowrap px-4 py-3 text-ink-600">{fmt(row.createdAt)}</td>
                        <td className="px-4 py-3">
                          <Pill tone={CATEGORY_TONE[row.category] ?? 'bg-ink-100 text-ink-700'}>
                            {CATEGORY_LABEL[row.category]}
                          </Pill>
                        </td>
                        <td className="px-4 py-3">
                          <Person p={row.subject} />
                        </td>
                        <td className="px-4 py-3">
                          <Person p={row.reporter} phone={false} />
                        </td>
                        <td className="min-w-[14rem] max-w-sm px-4 py-3 text-ink-700">
                          <p className="whitespace-pre-wrap break-words">{row.details || <span className="text-ink-400">No details</span>}</p>
                        </td>
                        <td className="px-4 py-3">
                          <Pill tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status]}</Pill>
                          {row.actionTaken && (
                            <div className="mt-1.5 min-w-[10rem] text-xs text-ink-600">
                              <span className="font-semibold">Action:</span> {row.actionTaken}
                              {row.actionedAt && <div className="text-ink-400">{fmt(row.actionedAt)}</div>}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {actionFor === row.id ? (
                            <form
                              className="flex min-w-[16rem] flex-wrap justify-end gap-1.5"
                              onSubmit={(e) => {
                                e.preventDefault()
                                void update(
                                  row,
                                  { status: 'ACTIONED', actionTaken: actionNote.trim() },
                                  `Action recorded on the report about ${row.subject.fullName}.`,
                                )
                              }}
                            >
                              <input
                                autoFocus
                                required
                                maxLength={500}
                                placeholder="What was done, e.g. account suspended"
                                value={actionNote}
                                onChange={(e) => setActionNote(e.target.value)}
                                className="input !py-1.5"
                              />
                              <button type="submit" disabled={busy || !actionNote.trim()} className="btn-sun !py-1.5">
                                Save
                              </button>
                              <button type="button" onClick={() => setActionFor(null)} className="btn-ghost !py-1.5">
                                Cancel
                              </button>
                            </form>
                          ) : (
                            <div className="flex flex-wrap justify-end gap-2">
                              {row.status === 'OPEN' && (
                                <button
                                  disabled={busy}
                                  onClick={() =>
                                    void update(row, { status: 'TRIAGED' }, `Report about ${row.subject.fullName} triaged.`)
                                  }
                                  className="btn-ghost !py-1.5"
                                >
                                  Triage
                                </button>
                              )}
                              <button
                                disabled={busy}
                                onClick={() => {
                                  setActionFor(row.id)
                                  setActionNote(row.actionTaken ?? '')
                                }}
                                className="btn-ghost whitespace-nowrap !py-1.5"
                              >
                                {row.status === 'ACTIONED' ? 'Edit action…' : 'Action taken…'}
                              </button>
                              {!closed && (
                                <button
                                  disabled={busy}
                                  onClick={() =>
                                    void update(row, { status: 'DISMISSED' }, `Report about ${row.subject.fullName} dismissed.`)
                                  }
                                  className="btn-ghost !py-1.5"
                                >
                                  Dismiss
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === 'accounts' && (
          <div className="card overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Kind</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Reports</th>
                  <th className="px-4 py-3">Blocked by</th>
                  <th className="px-4 py-3">Verification</th>
                </tr>
              </thead>
              <tbody>
                {accounts.length === 0 && empty(7, 'No account has been reported or blocked yet.')}
                {accounts.map((a) => (
                  <tr key={a.person.id} className="border-b border-ink-100 last:border-0">
                    <td className="px-4 py-3 font-medium text-ink-900">{a.person.fullName}</td>
                    <td className="px-4 py-3 text-ink-600">{humanize(a.person.entityKind)}</td>
                    <td className="break-all px-4 py-3 text-ink-700">{a.person.email ?? '-'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink-700">{a.person.phone ?? '-'}</td>
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className="font-semibold text-ink-900">{a.reports}</span>
                      {a.openReports > 0 && (
                        <span className="ml-2">
                          <Pill tone="bg-amber-100 text-amber-800">{a.openReports} open</Pill>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-ink-900">{a.blockedBy}</td>
                    <td className="px-4 py-3">
                      <Pill
                        tone={
                          a.verificationStatus === 'VERIFIED'
                            ? 'bg-green-100 text-green-800'
                            : a.verificationStatus === 'SUSPENDED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-ink-100 text-ink-600'
                        }
                      >
                        {humanize(a.verificationStatus)}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}
